import axios, { AxiosError, AxiosResponse } from 'axios';
import { getLogger } from '../diagnostics/logger';
import queryString from 'query-string';
import { getSimManagementErrorFromAxiosError } from '.';
import { PageParameters } from '../models/account';
import { QueryResult } from '../models/queryResult';
import { Sim, SimInfo, SimStatus, SmsHistoryResponse } from '../models/sim';
import { SimEventsResponse } from '../models/simEvents';
import { getApiBaseUrl } from '../services/helpers';
import { getWrapWithItems } from './tokeniser';
import { SimSearchOptions, defaultSimSearchOptions } from './utilities';
import config, { bundleNetworks } from '../config';

const log = getLogger('simManagement.api.simApi');

interface SimQueryOptions {
  id?: number;
  wrapWith?: string;
  [i: string]: any;
}

let cancelToken: any;

export function getBySerial(serial: string): Promise<Sim> {
  const url = config.API_BASE_URL + `api/sim/${serial}`;
  return new Promise<Sim>((resolve, reject) => {
    axios
      .get(url)
      .then((response: any) => {
        const result = {
          ...response.data,
          commencementDate: response.data.commencementDate ? Date.parse(response.data.commencementDate) : undefined
        } as Sim;

        resolve(result);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to load SIM ${serial}.`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function get(companyId: number, options: Partial<SimSearchOptions>): Promise<QueryResult<Sim>> {
  const resolvedOptions: SimSearchOptions = Object.assign(defaultSimSearchOptions, options);
  const { filters, sortModel } = options;
  let queryOptions: SimQueryOptions = {};

  if (filters && filters.length) {
    filters.forEach((filter: any) => {
      let searchField = '';
      switch (filter.id) {
        case 'accountTitle':
        case 'tariffNumber':
        case 'networkId':
        case 'puk':
        case 'order':
        case 'lastCommand':
        case 'provisioningOrder':
        case 'provisioningOrderNumber':
        case 'companyId':
          searchField = filter.id + 'StartsWith';
          queryOptions[searchField] = filter.value;
          break;
        case 'tariff':
        case 'serialNumber':
        case 'tel':
        case 'iccid':
        case 'order1':
        case 'order2':
        case 'order3':
        case 'order4':
        case 'order5':
        case 'tariffRange':
        case 'simType':
        case 'privateIp':
        case 'publicIp':
        case 'imei':
        case 'subportal':
          searchField = filter.id + 'Contains';
          queryOptions[searchField] = filter.value;
          break;
        case 'billingPeriodStart':
          searchField = filter.id + '>';
          queryOptions[searchField] = filter.value;
          break;
        case 'domVoiceUsage':
        case 'domSmsUsage':
        case 'intVoiceUsage':
        case 'intSmsUsage':
        case 'dataOverage':
        case 'accountId':
        case 'raNumber':
        case 'activeBundle':
        case 'operationInProgress':
        case 'inSession':
        case 'alertManagerEnabled':
          searchField = filter.id;
          queryOptions[searchField] = filter.value;
          break;
        case 'network':
          searchField = filter.id + 'StartsWith';
          queryOptions[searchField] = filter.value;
          break;
        case 'dataBundleMb':
        case 'dataUsageMb':
          // When submitting a range, the api is expecting the query parameter for a range to have the following format:
          // key>=1&key<=10
          // The issue we have is the stringify method in query-string package will always separate the key and value in the object with an equals symbol
          // to get around the issue, we put the range operator (> or <) with the object key, so the new object will look like:
          // {key>: 1, key<: 10}
          // now when the query param is serialised, the equals is placed after the key, like key>=1&key<=10
          if (filter.value.min) {
            queryOptions[filter.id + '>'] = filter.value.min;
          }

          if (filter.value.max) {
            queryOptions[filter.id + '<'] = filter.value.max;
          }

          break;
        case 'commencementDate':
          if (typeof filter.value === 'string') {
            queryOptions[filter.id + '>'] = filter.value;
            break;
          }

          if (filter.value.min) {
            queryOptions[filter.id + '>'] = filter.value.min;
          }

          if (filter.value.max) {
            queryOptions[filter.id + '<'] = filter.value.max;
          }

          break;
        case 'globalSearchTerm':
          searchField = filter.id;
          queryOptions[searchField] = filter.value;
          break;
        case 'tags':
          // the value is set by the tag column filter, but a persisted or stale filter may not match
          if (Array.isArray(filter.value?.tagIds) && filter.value.tagIds.length) {
            queryOptions['tagIds'] = filter.value.tagIds.join(',');
            queryOptions['tagMatchAll'] = filter.value.matchAll !== false;
          }
          break;
        case 'legacyStatus':
          searchField = 'status' + 'Contains';
          queryOptions[searchField] = filter.value;
          break;
        case 'status':
          // handled further down because we need to use some keys more than once, which we cannot do with the queryOptions object
          break;
        default:
          throw new Error('Column Id not recognised');
      }
    });
  }

  if (sortModel && sortModel.length) {
    sortModel.forEach((value) => {
      const orderByKey = value.desc ? 'orderByDesc' : 'orderBy';
      const orderByValue = value.id === 'legacyStatus' ? 'status' : value.id;
      queryOptions[orderByKey] = orderByValue;
    });
  } else {
    queryOptions['orderBy'] = 'stockId';
  }

  resolvedOptions.skip && (queryOptions.skip = resolvedOptions.skip);
  resolvedOptions.take && (queryOptions.take = resolvedOptions.take);

  queryOptions.skip = !queryOptions.skip ? 0 : queryOptions.skip;

  const wrapWithItems = getWrapWithItems(resolvedOptions);
  wrapWithItems.length > 0 && (queryOptions.wrapWith = wrapWithItems.join(','));

  let queryParams = queryString.stringify(queryOptions);

  const statusFilter = (filters as { id: string; value: string }[])?.find((x) => x.id === 'status');
  if (statusFilter) {
    queryParams += '&' + convertNewStatusToLegacyStatusQueryParam(statusFilter.value as SimStatus);
  }

  let url = resolvedOptions.accountId
    ? `${config.API_BASE_URL}api/company/${companyId}/account/${resolvedOptions.accountId}/sim?${queryParams}`
    : `${config.API_BASE_URL}api/company/${companyId}/sim?${queryParams}`;

  // remove equals sign after < or >
  url = url.replace('%3E=', '%3E').replace('%3C=', '%3C');

  if (cancelToken) {
    cancelToken.cancel();
  }

  const source = axios.CancelToken.source();
  cancelToken = source;

  return new Promise<QueryResult<Sim>>((resolve, reject) => {
    axios
      .get(url, { cancelToken: source.token })
      .then((response: AxiosResponse<QueryResult<Sim>>) => {
        response.data.result.forEach((sim: any) => addLegacyStatusToSim(sim));
        resolve(response.data);
      })
      .catch((error: any) => {
        if (axios.isCancel(error)) {
          //do nothing
        } else {
          const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
          log.error('Failed to load SIM list.', simManagementError);
          reject(simManagementError);
        }
      });
  });
}

export function getExpired(value: SimInfo[]): Promise<SimInfo[]> {
  const url = `${config.API_BASE_URL}api/sim/validate`;
  return new Promise<SimInfo[]>((resolve, reject) => {
    axios
      .post(url, value)
      .then((response: AxiosResponse<{ expired: SimInfo[] }>) => {
        resolve(response.data.expired);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load expired SIMs.', simManagementError);
        reject(simManagementError);
      });
  });
}

/**
 * Gets a list of SIMs from a company or account to which the user has access.
 * @param filter The filter parameters.
 */
export function exportSims(filter: PageParameters): Promise<Array<Sim>> {
  const { companyId, accountId } = filter;
  const apiBaseUrl = getApiBaseUrl(companyId, accountId);
  return new Promise<Array<Sim>>((resolve, reject) => {
    axios({
      url: `${apiBaseUrl}sim?export=true`,
      method: 'GET'
    })
      .then((response: any) => {
        response.data.forEach((sim: any) => addLegacyStatusToSim(sim));
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error(`Failed to get all SIMs for company ${companyId}, account ${accountId}`, simManagementError);
        reject(simManagementError);
      });
  });
}

export function GetSmsHistory(serialNo: string, pageNumber: number, pageSize: number): Promise<SmsHistoryResponse> {
  return new Promise<SmsHistoryResponse>((resolve, reject) => {
    axios
      .get(`${config.API_BASE_URL}api/sim/${serialNo}/smshistory`, {
        headers: {
          'x-pagenumber': pageNumber,
          'x-pagesize': pageSize
        }
      })
      .then((response: AxiosResponse<SmsHistoryResponse>) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        const simManagementError = getSimManagementErrorFromAxiosError(error as AxiosError);
        log.error('Failed to load SMS History Info.', simManagementError);
        reject(simManagementError);
      });
  });
}

const addLegacyStatusToSim = (sim: Sim) => {
  sim.legacyStatus = sim.status;

  const simSupportsBundleControl = bundleNetworks.includes(sim.networkId);
  const activeBundle = sim.activeBundle;

  switch (sim.legacyStatus) {
    case 'On Hire':
      sim.status = 'Provisioned';
      if (simSupportsBundleControl) {
        sim.status += ` ${activeBundle ? '(active)' : '(inactive)'}`;
      }
      break;
    case 'Available for Hire':
    case 'Not yet connected':
      sim.status = 'Not provisioned';
      if (simSupportsBundleControl) {
        sim.status += ` ${activeBundle ? '(active)' : '(inactive)'}`;
      }
      break;
    case 'Port-In Started':
    case 'Migrate-In Started':
      sim.status = 'Port-in started';
      break;
    case 'Awaiting RA':
      sim.status = 'Provisioned - Awaiting Tariff';
      break;
    default:
      sim.status = 'Other';
  }
};

const convertNewStatusToLegacyStatusQueryParam = (simStatus: SimStatus): string => {
  let filterValue = '';
  switch (simStatus) {
    case SimStatus.provisioned:
      filterValue = 'status=On Hire';
      return filterValue;
    case SimStatus.provisionedActive:
      filterValue = 'status=On Hire';
      filterValue += `&networkId=${bundleNetworks.join(',')}`;
      filterValue += '&activeBundle=true';
      return filterValue;
    case SimStatus.provisionedInactive:
      filterValue = 'status=On Hire';
      filterValue += `&networkId=${bundleNetworks.join(',')}`;
      filterValue += '&activeBundle=false';
      return filterValue;
    case SimStatus.notProvisioned:
      filterValue = 'status=Available for Hire,Not yet connected';
      return filterValue;
    case SimStatus.notProvisionedActive:
      filterValue = 'status=Available for Hire,Not yet connected';
      filterValue += `&networkId=${bundleNetworks.join(',')}`;
      filterValue += '&activeBundle=true';
      return filterValue;
    case SimStatus.notProvisionedInactive:
      filterValue = 'status=Available for Hire,Not yet connected';
      filterValue += `&networkId=${bundleNetworks.join(',')}`;
      filterValue += '&activeBundle=false';
      return filterValue;
    case SimStatus.portInStarted:
      return 'status=Port-In Started,Migrate-In Started';
    case SimStatus.awaitingProvision:
      return 'status=Awaiting RA';
    case SimStatus.other:
      return 'status=Other';
    default:
      return '';
  }
};

export function getSimEvents(serialNo: string, pageNumber: number, pageSize: number): Promise<SimEventsResponse> {
  return new Promise<SimEventsResponse>((resolve, reject) => {
    axios
      .get(`${config.API_BASE_URL}api/sim/${serialNo}/events`, {
        headers: {
          'x-pagenumber': pageNumber,
          'x-pagesize': pageSize
        }
      })
      .then((response: AxiosResponse<SimEventsResponse>) => {
        response.data.data = response.data.data.map((x: any) => ({
          ...x,
          details: JSON.parse(x.details as unknown as string)
        }));
        resolve(response.data);
      })
      .catch((error: any) => reject(getSimManagementErrorFromAxiosError(error as AxiosError)));
  });
}
